import type {
  CopilotProposal,
  CopilotProposalId,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import {
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const copilotProposalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The proposals one chat thread has produced, for the index the chat pins
     * above its composer. A summary per proposal — the editor fetches the
     * aggregates for the one proposal the user opens.
     */
    listThreadProposals: builder.query<
      readonly CopilotProposalSummary[],
      { threadId: string }
    >({
      query: ({ threadId }) => ({
        method: "GET",
        url: `/copilot/threads/${encodeURIComponent(threadId)}/proposals`,
      }),
      transformResponse: (response: {
        data: { proposals: readonly CopilotProposalSummary[] };
      }) => response.data.proposals,
      providesTags: (result, _error, { threadId }) =>
        getTagsProvidedByList(TagType.CopilotProposal, result, {
          filteredBy: [threadId],
        }),
    }),

    /**
     * One live signal proposal as stored, for the signal editor opened from
     * its proposal card. A resolved proposal is not found.
     */
    getPendingSignalProposal: builder.query<
      CopilotProposal<"signal">,
      CopilotProposalId
    >({
      query: (proposalId) => ({
        method: "GET",
        url: `/copilot/proposals/${encodeURIComponent(proposalId)}`,
        params: { resource_type: "signal" },
      }),
      transformResponse: (response: {
        data: { proposal: CopilotProposal<"signal"> };
      }) => response.data.proposal,
      providesTags: (_result, _error, proposalId) =>
        getTagsProvidedByEntity(TagType.CopilotProposal, proposalId),
    }),

    /**
     * One live automation proposal as stored, for the automation editor opened
     * from its proposal card. A resolved proposal is not found.
     */
    getPendingAutomationProposal: builder.query<
      CopilotProposal<"automation">,
      CopilotProposalId
    >({
      query: (proposalId) => ({
        method: "GET",
        url: `/copilot/proposals/${encodeURIComponent(proposalId)}`,
        params: { resource_type: "automation" },
      }),
      transformResponse: (response: {
        data: { proposal: CopilotProposal<"automation"> };
      }) => response.data.proposal,
      providesTags: (_result, _error, proposalId) =>
        getTagsProvidedByEntity(TagType.CopilotProposal, proposalId),
    }),

    /**
     * Resolve one live proposal — fired when the user applies or discards a
     * suggestion. The row is kept; only its status transitions.
     */
    resolveProposal: builder.mutation<
      void,
      {
        proposalId: CopilotProposalId;
        status: "applied" | "discarded";
      }
    >({
      query: ({ proposalId, status }) => ({
        method: "POST",
        url: `/copilot/proposals/${encodeURIComponent(proposalId)}/resolve`,
        body: { status },
      }),
      // The chat's proposal index lists this proposal, and an applied or
      // discarded one has no business staying on it.
      invalidatesTags: (_result, _error, { proposalId }) =>
        getTagsInvalidatedByUpdate(TagType.CopilotProposal, proposalId),
    }),
  }),
});

export const {
  useGetPendingAutomationProposalQuery,
  useGetPendingSignalProposalQuery,
  useListThreadProposalsQuery,
  useResolveProposalMutation,
} = copilotProposalApi;
